import React, { useMemo } from 'react';

// DEPENDENCIES
import isBoolean from 'lodash/isBoolean';
import { useFormikContext } from 'formik';

// GLOBAL VARIABLES
import { RETAIL_WHOLESALE, WHOLESALE_FORMAT } from 'codes';

// FUNCTIONS
import { formatDateTime } from 'functions';

// HELPERS
import useLookup from 'helpers/useLookup';

// GLOBAL COMPONENTS
import Card from 'parts/card/Card';
import Form from 'core/form/Form';
import HeaderData from '../../../../tools/HeaderData';
import AMFIProducts from './AMFIProducts';

// STYLES
import styles from './aftermarket.module.scss';

// RENDER
const AftermarketFinanceForm = () => {
  const { lookup: lenders } = useLookup({ lookup: 'lender' });
  const { values } = useFormikContext() || {};
  const {
    am_assignment_time, //
    fm_assignment_time,
    is_financed,
    dsl_lender_id,
    lender_text
  } = values || {};

  const fundedByType = useMemo(
    () => (isBoolean(is_financed) ? (is_financed ? 'Bank' : 'Cash') : undefined),
    [is_financed]
  );

  const isFundedByBank = useMemo(() => fundedByType === 'Bank', [fundedByType]);

  const fundedBy = useMemo(() => {
    if (!isFundedByBank) return 'Cash';
    if (dsl_lender_id === 0) return `Bank - Other - ${lender_text}`;
    const lenderName = (lenders || []).find(l => l.dsl_lender_id === dsl_lender_id)?.lender_name;
    if (lenderName) return `Bank - ${lenderName}`;
    return undefined;
  }, [dsl_lender_id, isFundedByBank, lender_text, lenders]);

  const amTimeAssigned = useMemo(() => formatDateTime(am_assignment_time, 4, true), [am_assignment_time]);
  const fmTimeAssigned = useMemo(() => formatDateTime(fm_assignment_time, 4, true), [fm_assignment_time]);

  return (
    <Form.Body>
      <Form.PlainText label="AM Assigment" name="am_assignment_user_full_name" />
      <Form.PlainText label="AM Time Assigned" value={amTimeAssigned} />
      <Form.Col>
        <AMFIProducts />
      </Form.Col>
      <Form.PlainText label="FM Assignment" name="fm_assignment_user_full_name" />
      <Form.PlainText label="FM Time Assigned" value={fmTimeAssigned} />
      <Form.PlainText label="Funded by" value={fundedBy} />
      {!isFundedByBank ? <Form.PlainText label="Funded Source" name="dsl_customer_source" /> : null}
      <Form.PlainText label="Customer Number" name="customer_number" />
      <Form.PlainText label="Tag State" name="tag_state_code" />
      <Form.PlainText label="Deal Number" name="deal_number" />
    </Form.Body>
  );
};

export default function AftermarketFinance(props) {
  const { values } = useFormikContext() || {};
  const {
    stock_num, //
    vehicle_year,
    make,
    model,
    sale_type_category_code,
    wholesale_format_id,
    process_through_finance
  } = values || {};

  const isWholesale = useMemo(
    () => sale_type_category_code === RETAIL_WHOLESALE.wholesale.category_code,
    [sale_type_category_code]
  );
  const isWholesaleSimplified = useMemo(
    () => isWholesale && wholesale_format_id === WHOLESALE_FORMAT.simplified.numeric,
    [isWholesale, wholesale_format_id]
  );

  return (
    <Card
      className={styles.card}
      title="Aftermarket / Finance"
      tools={<HeaderData stockNo={stock_num} year={vehicle_year} make={make} model={model} />}
      body={
        isWholesaleSimplified && process_through_finance === false ? (
          <div className="my-3 text-center large">Not processed through Finance</div>
        ) : (
          <AftermarketFinanceForm {...props} />
        )
      }
    />
  );
}
